<template>
  <TopBanner
    v-if="showBanner"
    :banner-header-text="platformMessageTranslation.subject"
    :banner-h-t-m-l-content="platformMessageTranslation.message"
    :banner-type="platformMessage.type"
    @close-top-banner="closeTopBanner"
  />

  <LoginFormContainer v-if="appConfig">
    <Transition :name="transitionName" mode="out-in">
      <!-- Screen 1: method selection -->
      <div
        v-if="screen === 'method-selection'"
        key="method-selection"
        class="login-screens"
      >
        <div class="login-method-list">
          <LoginMethodCard
            v-if="credentialMethods.length === 1"
            :icon="credentialMethods[0].icon"
            :title="credentialMethods[0].title"
            :subtitle="credentialMethods[0].subtitle"
            :badge="
              credentialMethods[0].value === lastUsedMethod
                ? trans('auth.lastUsed')
                : null
            "
            :data-test="`login-method-${credentialMethods[0].value}`"
            @click="selectCredential(credentialMethods[0].value)"
          />
          <OrgMethodCard
            v-else-if="credentialMethods.length > 1"
            icon="fa-lock"
            :options="credentialMethods"
            :last-used="credLastUsed"
            :title="trans('auth.methodTwoFactorTitle')"
            :subtitle="trans('auth.methodTwoFactorSubtitle')"
            data-test="login-method-credentials"
            @select="selectCredential"
          />
        </div>

        <div class="login-method-list mt-3">
          <OrgMethodCard
            v-if="organisationMethods.length > 0"
            :options="organisationOptions"
            :last-used="orgLastUsed"
            data-test="login-method-organisations"
            @select="navigateToOrg"
          />

          <LoginMethodCard
            v-if="hasSiths"
            icon="fa-credit-card-front"
            :title="trans('auth.methodSithsTitle')"
            :subtitle="trans('auth.methodSithsSubtitle')"
            :badge="lastUsedMethod === 'siths' ? trans('auth.lastUsed') : null"
            data-test="login-method-siths"
            @click="selectSiths"
          />
        </div>
      </div>

      <!-- Screen 2: credentials -->
      <div
        v-else-if="screen === 'credentials'"
        key="credentials"
        class="login-screens"
      >
        <a
          href="#"
          class="btn-back"
          @click.prevent="goBack('method-selection')"
        >
          <i class="fa fa-arrow-left"></i>
          {{ trans("auth.backToMethods") }}
        </a>
        <CredentialsForm
          :method="selectedMethod"
          @success="onCredentialsSuccess"
        />
      </div>

      <!-- Screen 3: MFA -->
      <div v-else-if="screen === 'mfa'" key="mfa" class="login-screens">
        <a href="#" class="btn-back" @click.prevent="goBack('credentials')">
          <i class="fa fa-arrow-left"></i>
          {{ trans("auth.backToCredentials") }}
        </a>
        <MfaForm :method="selectedMethod" :token="authToken" />
      </div>
    </Transition>
  </LoginFormContainer>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAsyncState } from "@vueuse/core";
import { useStore } from "vuex";
import { lang, getBrowserLocale } from "../../i18n";
import { appConfigGet } from "../../app/practitioner-portal/store/app-config";
import { AUTH_METHOD_STORAGE_KEY } from "./helpers/authStorageKeys";
import LoginFormContainer from "./LoginFormContainer.vue";
import LoginMethodCard from "./LoginMethodCard.vue";
import OrgMethodCard from "./OrgMethodCard.vue";
import CredentialsForm from "./CredentialsForm.vue";
import MfaForm from "./MfaForm.vue";
import TopBanner from "../Utils/TopBanner.vue";

const store = useStore();
const router = useRouter();

const screen = ref("method-selection");
const transitionName = ref("slide-left");
const selectedMethod = ref(null);
const authToken = ref(null);
const lastUsedMethod = localStorage.getItem(AUTH_METHOD_STORAGE_KEY);

const languages = ["de", "en", "fr", "it", "nl", "sv"];

const { state: appConfig } = useAsyncState(appConfigGet(), undefined, {
  onSuccess: () => {
    lang.setLocale(userBrowserOrFallbackLocale());
    if (
      lastUsedMethod &&
      credentialMethods.value.some((m) => m.value === lastUsedMethod)
    ) {
      selectedMethod.value = lastUsedMethod;
      screen.value = "credentials";
    }
  },
});

const userBrowserOrFallbackLocale = () => {
  const userLocale = store.state.user?.culture;
  const browserLocale = getBrowserLocale();
  const platformLocale = appConfig.value?.data.platform.locale ?? "en";
  if (userLocale && languages.includes(userLocale)) return userLocale;
  return languages.includes(browserLocale) ? browserLocale : platformLocale;
};

const allMethods = computed(() => appConfig.value?.data.authMethods ?? []);

const credentialMethods = computed(() =>
  allMethods.value
    .filter((m) => m.startsWith("username-password:"))
    .map((m) => ({
      value: m,
      icon: m === "username-password:sms" ? "fa-mobile" : "fa-lock",
      title: lang.get(
        m === "username-password:sms"
          ? "auth.methodSmsTitle"
          : "auth.methodAuthAppTitle",
      ),
      subtitle: lang.get(
        m === "username-password:sms"
          ? "auth.methodSmsSubtitle"
          : "auth.methodAuthAppSubtitle",
      ),
    })),
);

const hasSiths = computed(() => allMethods.value.includes("siths"));

const organisationMethods = computed(() =>
  allMethods.value.filter(
    (m) => !m.startsWith("username-password:") && m !== "siths",
  ),
);

const organisationOptions = computed(() =>
  organisationMethods.value.map((m) => ({
    value: m,
    title: lang.get("general.authentication-method." + m),
  })),
);

const credLastUsed = computed(() =>
  credentialMethods.value.some((m) => m.value === lastUsedMethod)
    ? lastUsedMethod
    : null,
);

const orgLastUsed = computed(() =>
  organisationMethods.value.includes(lastUsedMethod) ? lastUsedMethod : null,
);

const orgUrls = {
  eksjo: "/auth/saml/eksjo/login",
  rjl: "/auth/saml/rjl/login",
  sll: "/auth/saml/sll/login",
  jonkoping: "/auth/svensk-e-identitet/jonkoping",
};

const navigateToOrg = (method) => {
  const url = orgUrls[method];
  if (url) {
    localStorage.setItem(AUTH_METHOD_STORAGE_KEY, method);
    window.location.href = url;
  }
};

const selectCredential = (method) => {
  selectedMethod.value = method;
  localStorage.setItem(AUTH_METHOD_STORAGE_KEY, method);
  transitionName.value = "slide-left";
  screen.value = "credentials";
};

const selectSiths = () => {
  localStorage.setItem(AUTH_METHOD_STORAGE_KEY, "siths");
  router.push("/auth/siths");
};

const onCredentialsSuccess = (token) => {
  authToken.value = token;
  transitionName.value = "slide-left";
  screen.value = "mfa";
};

const goBack = (target) => {
  transitionName.value = "slide-right";
  screen.value = target;
};

const platformMessage = computed(
  () => appConfig.value?.data.platformMessage ?? false,
);

const platformMessageTranslation = computed(() => {
  if (platformMessage.value?.translation)
    return platformMessage.value.translation;
  return {};
});

const seenPlatformMessages = () =>
  localStorage.getItem("seen_platform_messages") ?? [];

const showBanner = computed(
  () =>
    platformMessage.value &&
    !seenPlatformMessages().includes(platformMessage.value.id),
);

const closeTopBanner = () => {
  localStorage.setItem("seen_platform_messages", [
    ...seenPlatformMessages(),
    platformMessage.value.id,
  ]);
};
</script>
